import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Checkbox,
  useTheme,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { LogisticInvoice } from "../../QueueBoard/models/logisticsTypes";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GoogleMapsAutocomplete from "./GoogleMapsAutocomplete";

type Props = {
  workOrders: LogisticInvoice[];
  selectedWorkOrders: number[];
  onToggle: (id: number) => void;
  onPrint: () => void;
  printing: boolean;
  onUpdateAddressText?: (id: number, address: string) => void;
  onUpdateAddress?: (id: number, address: string) => void;
  addresses?: Map<number, string>;
  onAddressSelect?: (
    id: number,
    address: string,
    coordinates?: { lat: number; lng: number }
  ) => void;
  showSelectionCheckbox?: boolean;
  invalidAddressIds?: Set<number>;
  isDisabled?: boolean;
};

const LogisticsList: React.FC<Props> = ({
  workOrders,
  selectedWorkOrders,
  onToggle,
  onPrint,
  printing,
  onUpdateAddressText,
  onUpdateAddress,
  addresses,
  onAddressSelect,
  showSelectionCheckbox = true,
  invalidAddressIds,
  isDisabled = false,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{ height: "100%", opacity: isDisabled ? 0.6 : 1 }}
      aria-disabled={isDisabled}
    >
      <CardContent sx={{ pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            mb: 2,
          }}
        >
          <Chip sx={{
            borderRadius: "5px",
            color: "white",
            backgroundColor: "#1976d2",
            fontWeight: 600,
          }} label="Work Orders / Rental Contracts" variant="outlined" size="small" />
          <Button
            variant="contained"
            startIcon={<PrintIcon sx={{ color: "white" }} />}
            onClick={onPrint}
            disabled={isDisabled || selectedWorkOrders.length === 0 || printing}
            sx={{
              bgcolor: "#2371E7",
              color: "white",
              textTransform: "capitalize",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                bgcolor: "#1e5bb8",
              },
            }}
          >
            {printing ? "Generating Print..." : "Print Map & Details"}
          </Button>
        </Box>

        {isDisabled && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 1, display: "block" }}
          >
            Please search and select a valid starting address to enable invoice
            selection.
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select invoices to add to Google Maps route
        </Typography>

        <Box
          sx={{
            maxHeight: { xs: 360, md: "calc(100vh - 290px)" },
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            position: "relative",
            pb: 2,
          }}
        >
          {workOrders.map((workOrder) => (
            <Card
              key={workOrder.invoiceDocumentIdentity}
              sx={{
                mb: 1,
                borderRadius: 2,
                border: selectedWorkOrders.includes(
                  workOrder.invoiceDocumentIdentity
                )
                  ? `2px solid ${theme.palette.primary.main}`
                  : "1px solid #e5e7eb",
                boxShadow: 0,
                "&:hover": { boxShadow: 2 },
                overflow: "visible",
                position: "relative",
              }}
            >
              <CardContent
                sx={{
                  p: 1.5,
                  "&:last-child": { pb: 1.5 },
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "flex-start" },
                    gap: 1,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  {showSelectionCheckbox && (
                    <Checkbox
                      checked={selectedWorkOrders.includes(
                        workOrder.invoiceDocumentIdentity
                      )}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggle(workOrder.invoiceDocumentIdentity);
                      }}
                      size="small"
                      sx={{ mb: 2.5, p: "2px" }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", md: "center" },
                        flexDirection: { xs: "column", md: "row" },
                        gap: { xs: 0.5, md: 0 },
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                        >
                          #{workOrder.invoiceNumber}
                        </Typography>
                        {!!workOrder.workOrderDescription && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: { xs: "100%", md: 260 },
                            }}
                            title={workOrder.workOrderDescription}
                          >
                            {workOrder.workOrderDescription}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                        {selectedWorkOrders.includes(workOrder.invoiceDocumentIdentity) && (
                          <Chip
                            label={selectedWorkOrders.indexOf(workOrder.invoiceDocumentIdentity) + 2}
                            size="small"
                            sx={{
                              borderRadius:'5px',
                              minWidth: 24,
                              height: 24,
                              bgcolor: '#1976d2',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              "& .MuiChip-label": { px: 0.5 },
                            }}
                          />
                        )}
                        {!!workOrder.invoiceType && (
                          <Chip
                            label={workOrder.invoiceType}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: "5px",
                              backgroundColor:
                                workOrder.invoiceType === "Rental Contract"
                                  ? "#4caf50"
                                  : "#1976d2",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {workOrder.customerName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.25 }}
                    >
                      {workOrder.technicianName}
                    </Typography>
                    {workOrder.createdDate && (
                      <Stack
                        sx={{ mb: 0.5 }}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 14, color: "#667EEA" }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.25, display: "block" }}
                        >
                          Created:{" "}
                          {new Date(workOrder.createdDate).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    )}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      mb="5px"
                      sx={{
                        overflow: "visible",
                        position: "relative",
                        zIndex: 1,
                        minHeight: 40, // Ensure minimum height for address field
                        flexWrap: "nowrap",
                        width: "100%",
                      }}
                    >
                      <LocationOnIcon
                        sx={{
                          fontSize: 14,
                          color: "#667EEA",
                          flexShrink: 0,
                        }}
                      />
                      <GoogleMapsAutocomplete
                        value={(() => {
                          // Clean address - remove line breaks and normalize whitespace
                          const rawAddress =
                            (addresses &&
                              addresses.get(
                                workOrder.invoiceDocumentIdentity
                              )) ??
                            workOrder.customerAddress ??
                            "";
                          if (!rawAddress) return "";
                          // Replace \r, \n, \r\n with space, then normalize multiple spaces to single space, trim
                          return rawAddress
                            .replace(/\r\n/g, " ")
                            .replace(/\r/g, " ")
                            .replace(/\n/g, " ")
                            .replace(/\s+/g, " ")
                            .trim();
                        })()}
                        onChange={(value) => {
                          // Only update the address text, don't trigger map updates
                          onUpdateAddressText &&
                            onUpdateAddressText(
                              workOrder.invoiceDocumentIdentity,
                              value
                            );

                          // Unselect the work order when user starts typing
                          if (
                            selectedWorkOrders.includes(
                              workOrder.invoiceDocumentIdentity
                            )
                          ) {
                            onToggle(workOrder.invoiceDocumentIdentity);
                          }
                        }}
                        onSelect={(address, coordinates) => {
                          // Update address immediately when selection is made
                          if (onUpdateAddress) {
                            onUpdateAddress(
                              workOrder.invoiceDocumentIdentity,
                              address
                            );
                          }
                          if (onAddressSelect) {
                            onAddressSelect(
                              workOrder.invoiceDocumentIdentity,
                              address,
                              coordinates
                            );
                          }
                        }}
                        placeholder="Enter address..."
                        size="small"
                        fullWidth={true}
                        disabled={isDisabled}
                        inputProps={{
                          style: { fontSize: 12 },
                          onClick: (e: any) => e.stopPropagation(),
                          onMouseDown: (e: any) => e.stopPropagation(),
                          onFocus: (e: any) => e.stopPropagation(),
                          title:
                            invalidAddressIds &&
                            invalidAddressIds.has(
                              workOrder.invoiceDocumentIdentity
                            )
                              ? "Invalid address. Please select a valid suggestion."
                              : undefined,
                        }}
                        sx={{
                          mb: 1,
                          width: "100%",
                          position: "relative",
                          zIndex: 2,
                          minWidth: 0,
                          flexGrow: 1,
                          "& .MuiTextField-root": {
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "background.paper",
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            },
                          },
                          ...(invalidAddressIds &&
                          invalidAddressIds.has(
                            workOrder.invoiceDocumentIdentity
                          )
                            ? {
                                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: (theme: any) =>
                                      theme.palette.error.main,
                                  },
                                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: (theme: any) =>
                                      theme.palette.error.dark,
                                  },
                                "& .MuiInputLabel-root": {
                                  color: (theme: any) =>
                                    theme.palette.error.main,
                                },
                              }
                            : {}),
                        }}
                      />
                    </Stack>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {!!workOrder.status && (
                        <Chip
                          label={
                            workOrder.status
                              ? workOrder.status.charAt(0).toUpperCase() +
                                workOrder.status.slice(1)
                              : ""
                          }
                          size="small"
                          variant="outlined"
                          sx={{
                            // backgroundColor: workOrder.workOrderBackColor,
                            // color: workOrder.workOrderForeColor,
                            fontSize: "0.7rem",
                          }}
                        />
                      )}
                      {!!workOrder.invoicestatus && (
                        <Chip
                          label={workOrder.invoicestatus}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: workOrder.workOrderBackColor,
                            color: workOrder.workOrderForeColor,
                            fontSize: "0.7rem",
                          }}
                        />
                      )}
                    </Box>
                    {/* Removed quoted and clocked hours from UI as requested */}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LogisticsList;
